import { accessLapse } from "../accessLapse";
import { hasAccess } from "../subscriptionState";
import { useCurrentSubscription } from "./useCurrentSubscription";
import { usePrepaidTerms } from "./usePrepaidTerms";
import type { AccessLapse } from "../accessLapse";

/** What a backend with prepaid billing switched off amounts to: no term can
 *  exist, so there is none to find. */
const NO_TERMS = { current: null, queued: null };

export interface AccessLapseState {
  /**
   * Whether the product is shut.
   *
   * True only once the billing read has answered and said there is neither a
   * plan running nor a trial. A read still out, or one that failed, leaves
   * everything open: not knowing is not the same as knowing they have not
   * paid, and a paying practice must never find its own workspace shut because
   * a call was slow.
   */
  locked: boolean;
  /** What ran out, where it can be told — see `accessLapse`. */
  lapse: AccessLapse;
}

/**
 * The two reads behind "what ran out", in one place.
 *
 * Two calls rather than one because no single endpoint answers it: the
 * subscription call says whether the door is open and carries the trial, and
 * only the prepaid terms say whether anything was ever bought. Both go through
 * TanStack Query on keys their own hooks own, so the three screens that ask
 * this share one of each rather than issuing six.
 *
 * The terms are allowed to fail. They are owner-only, so a staff member gets a
 * `403` from them and this answers `null` for the lapse — the ordinary offer,
 * which is true of every workspace with the door shut, rather than a guess at
 * which thing ended.
 *
 * One failure is not a gap, though: `404 prepaid_billing_unavailable` says the
 * backend has prepaid billing switched off, and a practice cannot own a term
 * the product does not sell. That reads as no terms rather than as no answer,
 * which is what keeps the trial's own wording on the screens of a backend
 * running without the flag.
 */
export function useAccessLapse(): AccessLapseState {
  const { subscription, trial, accessSource, status } =
    useCurrentSubscription();
  const terms = usePrepaidTerms();

  const locked = status === "ready" && !hasAccess(accessSource);

  return {
    locked,
    lapse: accessLapse({
      locked,
      subscription,
      trial,
      terms:
        terms.status === "ready"
          ? { current: terms.current, queued: terms.queued }
          : terms.unavailable
            ? NO_TERMS
            : null,
    }),
  };
}
